import { assertNever } from "@reagent/sim";
import type { ReagentShape } from "../../reagents";

/**
 * The silhouette half of a reagent's identity (see `src/reagents.ts`).
 *
 * These are deliberately blunt primitives read at about 12 screen pixels on a
 * moving packet: the test is not "is that a torus" but "is that the same thing
 * I saw leave the tank". Anything with fine detail fails that test, and
 * anything that reads only by colour fails it for a colour-blind player.
 *
 * The switch is exhaustive over ReagentShape, so adding a shape without giving
 * it a mesh is a compile error rather than an invisible packet — the same rule
 * the FX registry and HardwareNode follow.
 */
export function ReagentGeometry({ shape, size }: { shape: ReagentShape; size: number }) {
  switch (shape) {
    case "sphere":
      return <sphereGeometry args={[size, 12, 12]} />;
    case "cube":
      return <boxGeometry args={[size * 1.6, size * 1.6, size * 1.6]} />;
    case "diamond":
      return <octahedronGeometry args={[size * 1.35, 0]} />;
    case "rod":
      return <capsuleGeometry args={[size * 0.55, size * 1.9, 4, 8]} />;
    case "ring":
      return <torusGeometry args={[size * 0.95, size * 0.42, 8, 14]} />;
    case "wedge":
      return <tetrahedronGeometry args={[size * 1.5, 0]} />;
    case "cross":
      // No cross primitive exists; two crossed bars is the cheapest thing that
      // still reads as an X from the fixed isometric angle.
      return <boxGeometry args={[size * 2.4, size * 0.7, size * 0.7]} />;
    default:
      return assertNever(shape, "ReagentGeometry");
  }
}

/**
 * A whole reagent marker: geometry plus the emissive toon material the packets
 * and buffer contents share. `cross` gets its second bar here rather than in
 * the geometry switch, which can only return one.
 */
export function ReagentGlyph({
  shape,
  color,
  size = 0.09,
  opacity = 1,
}: {
  shape: ReagentShape;
  color: string;
  size?: number;
  opacity?: number;
}) {
  const material = (
    <meshToonMaterial
      color={color}
      emissive={color}
      emissiveIntensity={0.5}
      transparent={opacity < 1}
      opacity={opacity}
    />
  );

  return (
    <group>
      <mesh>
        <ReagentGeometry shape={shape} size={size} />
        {material}
      </mesh>
      {shape === "cross" && (
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <boxGeometry args={[size * 2.4, size * 0.7, size * 0.7]} />
          {material}
        </mesh>
      )}
    </group>
  );
}
